import React from 'react'
import PropTypes from 'prop-types'

// Components
import {Alert, Box, CloseButton, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'

// Constants
import {PAYMENT_METHODS, GC_ACTION_ERROR_TYPE} from '@app/constants'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useIntl} from 'react-intl'
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'

// Utils
import {defineMessages} from 'react-intl'

const checkGiftCardMessages = defineMessages({
    [GC_ACTION_ERROR_TYPE.GC_EXPIRED]: {
        id: 'giftcard.code.expired',
        defaultMessage: 'This Gift Card has expired.'
    },
    [GC_ACTION_ERROR_TYPE.GC_NOT_EXIST]: {
        id: 'giftcard.code.invalid',
        defaultMessage:
            'We cannot identify the number you entered, please check your online gift card number has been entered correctly.'
    },
    [GC_ACTION_ERROR_TYPE.GC_NO_BALANCE]: {
        id: 'giftcard.code.noBalance',
        defaultMessage: 'There is nothing left on this Gift Card.'
    }
})

/**
 * Component to check and display the status of a gift card code.
 * It verifies the existence, expiration, and balance of a gift card,
 * and provides appropriate feedback to the user.
 *
 * @param {Object} props.control - The control object used for form management.
 * @param {string} props.code - The gift card code to be checked.
 * @param {Function} props.onClose - Callback function to handle the closing of the component.
 * @param {React.ReactNode} props.children - Additional elements or components to render within the component.
 *
 * @returns {React.ReactNode|null} - Returns a React component displaying the status of the gift card or null if the code is invalid.
 *
 * @example
    const {checkGiftCardBalance} = useGiftCardActions(basket)

    const [gcCode, setGcCode] = useState('')
    const [checkGiftCardData, setCheckGiftCardData] = useState({})

    const onSubmit = async (values) => {
        setGcCode(values.giftcardCode)
        setCheckGiftCardData(await checkGiftCardBalance(values.giftcardCode))
    }
    const resetForm = () => {
        setGcCode('')
        setCheckGiftCardData({})
    }

    <CheckGiftcardCode
        code={gcCode}
        checkGiftCardData={checkGiftCardData}
        onClose={clearGiftcard}
    >
        <AdditionalInfo />
    </CheckGiftcardCode>
 */
export const CheckGiftcardCode = ({
    code,
    checkGiftCardData,
    onClose,
    subtractPaymentInstrumentAmount = false,
    children
}) => {
    const {data: giftCard, error: giftCardError} = checkGiftCardData

    const styles = useStyleConfig('Giftcard')
    const intl = useIntl()
    const {data: basket} = useCurrentBasket()

    // If there is no data, return null.
    // `checkGiftCardData` can be set to `{}` to hide the component without using another  state variable to control the visibility.
    if (Object.keys(checkGiftCardData).length === 0) return null

    if (giftCardError) {
        return (
            <Text {...styles.error} aria-live="polite">
                <FormattedMessage {...checkGiftCardMessages[giftCardError]} />
            </Text>
        )
    }

    let gcBalance = giftCard?.balance

    // If we are at the checkout and we have already applied this Gift Card to the basket
    // Find the current Gift Card in the basket and subtract the amount that has been applied
    if (subtractPaymentInstrumentAmount && basket?.paymentInstruments?.length) {
        const currentGC = basket?.paymentInstruments?.find(
            ({paymentMethodId, maskedGiftCertificateCode}) =>
                paymentMethodId === PAYMENT_METHODS.GIFT_CERTIFICATE &&
                maskedGiftCertificateCode === giftCard.maskedGiftCertificateCode
        )
        gcBalance -= currentGC?.amount ?? 0
    }

    // GC exists and has balance, but we already applied all of it to this basket
    if (gcBalance === 0) {
        return (
            <Text {...styles.error} aria-live="polite">
                <FormattedMessage
                    defaultMessage="There is no more value left on this online gift card."
                    id="giftcard.code.alreadyApplied"
                />
            </Text>
        )
    }

    // GC exists and has balance
    return (
        <Alert variant="cardInfos" mt="8">
            <Box flex="1" fontSize="sm">
                <Text fontWeight="medium" mb="8">
                    {code}
                </Text>
                <FormattedMessage
                    defaultMessage="This Gift Card has {balance} remaining"
                    id="giftcard.check.balance"
                    values={{
                        balance: intl.formatNumber(gcBalance, {
                            style: 'currency',
                            currency: giftCard?.c_giftCertificateCurrencyCode,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })
                    }}
                />
                {children}
            </Box>

            <CloseButton
                alignSelf="flex-start"
                position="relative"
                w="12px"
                h="12px"
                fontSize="12px"
                onClick={onClose}
            />
        </Alert>
    )
}

CheckGiftcardCode.propTypes = {
    code: PropTypes.string.isRequired,
    children: PropTypes.node,
    onClose: PropTypes.func,
    checkGiftCardData: PropTypes.shape({
        data: PropTypes.object,
        error: PropTypes.string
    }).isRequired,
    subtractPaymentInstrumentAmount: PropTypes.bool
}
